import {
  collection, doc, addDoc, getDocs, updateDoc, deleteDoc,
  query, where, Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { ProjectDocument } from '../models/types';
import { withTimeout } from './utils';

const DOCS = 'projectDocuments';

function mapDoc(id: string, data: Record<string, any>): ProjectDocument {
  return {
    id,
    projectId: data.projectId ?? '',
    name: data.name ?? '',
    type: data.type ?? 'Other',
    url: data.url ?? '',
    assetDescription: data.assetDescription ?? undefined,
    system: data.system ?? undefined,
    notes: data.notes ?? undefined,
    addedBy: data.addedBy ?? undefined,
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() ?? data.updatedAt ?? '',
  };
}

export const createProjectDocument = async (
  input: Omit<ProjectDocument, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const now = Timestamp.now();
  const clean: Record<string, any> = {};
  Object.entries(input).forEach(([k, v]) => { if (v !== undefined) clean[k] = v; });
  const ref = await withTimeout(addDoc(collection(db, DOCS), { ...clean, createdAt: now, updatedAt: now }));
  return ref.id;
};

export const getProjectDocuments = async (projectId: string): Promise<ProjectDocument[]> => {
  const q = query(collection(db, DOCS), where('projectId', '==', projectId));
  const snap = await withTimeout(getDocs(q));
  return snap.docs
    .map(d => mapDoc(d.id, d.data()))
    .sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
};

export const updateProjectDocument = async (
  id: string,
  partial: Partial<Omit<ProjectDocument, 'id' | 'projectId' | 'createdAt'>>
): Promise<void> => {
  const clean: Record<string, any> = { updatedAt: Timestamp.now() };
  Object.entries(partial).forEach(([k, v]) => { if (v !== undefined) clean[k] = v; });
  await withTimeout(updateDoc(doc(db, DOCS, id), clean));
};

export const deleteProjectDocument = async (id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, DOCS, id)));
};
